import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from './apiBaseUrl';
import './ESignPage.css';

const DEFAULT_SIGNATURE = {
    pageNo: '1',
    signaturePosition: 'BOTTOMRIGHT',
    height: 70,
    width: 130,
    xCoordinate: 100,
    yCoordinate: 200
};

const CERTIFICATE_DEFAULTS = {
    signatureOptions: 'docSignerClass3',
    signerName: '',
    signerDesignation: '',
    certificateType: 'ORG',
    reason: 'Loan Agreement Signing',
    location: 'Delhi'
};

const SIGN_DEFAULTS = {
    reason: 'Loan Agreement',
    location: 'Delhi',
    signatureOptions: 'docSignerClass3',
    certificateId: '',
    logoUrl: '',
    signerName: '',
    signerDesignation: '',
    certificateType: 'ORG',
    fileTtl: '2 days'
};

function ESignPage() {
    const navigate = useNavigate();
    const [pdfFile, setPdfFile] = useState(null);
    const [signForm, setSignForm] = useState(SIGN_DEFAULTS);
    const [signature, setSignature] = useState(DEFAULT_SIGNATURE);
    const [certificateForm, setCertificateForm] = useState(CERTIFICATE_DEFAULTS);
    const [signing, setSigning] = useState(false);
    const [creatingCertificate, setCreatingCertificate] = useState(false);
    const [signResult, setSignResult] = useState(null);
    const [signError, setSignError] = useState('');
    const [certificateResult, setCertificateResult] = useState('');
    const [certificateError, setCertificateError] = useState('');

    const updateSignForm = (event) => {
        const { name, value } = event.target;
        setSignForm((current) => ({ ...current, [name]: value }));
    };

    const updateSignature = (event) => {
        const { name, value } = event.target;
        setSignature((current) => ({ ...current, [name]: value }));
    };

    const updateCertificateForm = (event) => {
        const { name, value } = event.target;
        setCertificateForm((current) => ({ ...current, [name]: value }));
    };

    const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            const base64 = result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read PDF file'));
        reader.readAsDataURL(file);
    });

    const handleSignSubmit = async (event) => {
        event.preventDefault();
        setSignError('');
        setSignResult(null);

        if (!pdfFile) {
            setSignError('Select a PDF file first.');
            return;
        }

        setSigning(true);
        try {
            const pdf = await readFileAsBase64(pdfFile);
            const payload = {
                pdf,
                signatureOptions: signForm.signatureOptions,
                certificateId: signForm.certificateId || undefined,
                logoUrl: signForm.logoUrl || undefined,
                signerName: signForm.signerName || undefined,
                signerDesignation: signForm.signerDesignation || undefined,
                certificateType: signForm.certificateType || undefined,
                fileTtl: signForm.fileTtl || undefined,
                reason: signForm.reason || undefined,
                location: signForm.location || undefined,
                signatures: [
                    {
                        pageNo: [signature.pageNo || '1'],
                        signaturePosition: [signature.signaturePosition],
                        height: Number(signature.height) || 70,
                        width: Number(signature.width) || 130,
                        ...(signature.signaturePosition === 'CUSTOMIZE'
                            ? {
                                xCoordinate: [Number(signature.xCoordinate) || 100],
                                yCoordinate: [Number(signature.yCoordinate) || 200]
                            }
                            : {})
                    }
                ]
            };

            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/esign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || `eSign failed with HTTP ${response.status}`);
            }
            setSignResult(data);
        } catch (error) {
            setSignError(error.message || 'eSign request failed');
        } finally {
            setSigning(false);
        }
    };

    const handleCertificateSubmit = async (event) => {
        event.preventDefault();
        setCertificateError('');
        setCertificateResult('');
        setCreatingCertificate(true);
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/esign/certificate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(certificateForm)
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || `Certificate request failed with HTTP ${response.status}`);
            }
            setCertificateResult(data.certificateId || '');
            setSignForm((current) => ({ ...current, certificateId: data.certificateId || current.certificateId }));
        } catch (error) {
            setCertificateError(error.message || 'Certificate request failed');
        } finally {
            setCreatingCertificate(false);
        }
    };

    return (
        <div className="esign-page">
            {(signing || creatingCertificate) ? (
                <div className="app-page-loader-overlay" aria-live="polite" aria-busy="true">
                    <div className="app-page-loader-card">
                        <span className="app-page-loader-spinner" aria-hidden="true"></span>
                        <p>{signing ? 'Signing your PDF...' : 'Generating certificate ID...'}</p>
                    </div>
                </div>
            ) : null}
            <div className="esign-shell">
                <div className="esign-hero">
                    <button type="button" className="esign-back" onClick={() => navigate(-1)}>Back</button>
                    <div>
                        <p className="esign-kicker">Signzy DocSigner</p>
                        <h1>Upload, sign, and fetch the signed PDF URL</h1>
                        <p className="esign-subtitle">This page converts a PDF to base64 in the browser, sends it to the backend proxy, and returns the signed document link.</p>
                    </div>
                </div>

                <div className="esign-grid">
                    <section className="esign-card">
                        <div className="esign-card-head">
                            <h2>Sign PDF</h2>
                            <p>Uses `POST /api/esign`. Pass a certificate ID explicitly or leave it blank and let the backend generate one from signer details.</p>
                        </div>
                        <form className="esign-form" onSubmit={handleSignSubmit}>
                            <label className="esign-field">
                                <span>PDF file</span>
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(event) => setPdfFile(event.target.files?.[0] || null)}
                                />
                            </label>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Certificate ID</span>
                                    <input name="certificateId" value={signForm.certificateId} onChange={updateSignForm} placeholder="64f8c2..." />
                                </label>
                                <label className="esign-field">
                                    <span>Signature option</span>
                                    <select name="signatureOptions" value={signForm.signatureOptions} onChange={updateSignForm}>
                                        <option value="docSignerClass3">docSignerClass3</option>
                                        <option value="docSignerClass2">docSignerClass2</option>
                                    </select>
                                </label>
                            </div>

                            <label className="esign-field">
                                <span>Logo URL</span>
                                <input name="logoUrl" value={signForm.logoUrl} onChange={updateSignForm} placeholder="https://yourdomain.com/logo.png" />
                            </label>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Signer name</span>
                                    <input name="signerName" value={signForm.signerName} onChange={updateSignForm} placeholder="Your Company Name" />
                                </label>
                                <label className="esign-field">
                                    <span>Signer designation</span>
                                    <input name="signerDesignation" value={signForm.signerDesignation} onChange={updateSignForm} placeholder="Authorized Signatory" />
                                </label>
                            </div>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Reason</span>
                                    <input name="reason" value={signForm.reason} onChange={updateSignForm} />
                                </label>
                                <label className="esign-field">
                                    <span>Location</span>
                                    <input name="location" value={signForm.location} onChange={updateSignForm} />
                                </label>
                            </div>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Page No</span>
                                    <input name="pageNo" value={signature.pageNo} onChange={updateSignature} placeholder="1 / LAST / ALL" />
                                </label>
                                <label className="esign-field">
                                    <span>Certificate type</span>
                                    <select name="certificateType" value={signForm.certificateType} onChange={updateSignForm}>
                                        <option value="ORG">ORG</option>
                                        <option value="INDIVIDUAL">INDIVIDUAL</option>
                                    </select>
                                </label>
                                <label className="esign-field">
                                    <span>Position</span>
                                    <select name="signaturePosition" value={signature.signaturePosition} onChange={updateSignature}>
                                        <option value="TOPLEFT">TOPLEFT</option>
                                        <option value="TOPCENTER">TOPCENTER</option>
                                        <option value="TOPRIGHT">TOPRIGHT</option>
                                        <option value="MIDDLELEFT">MIDDLELEFT</option>
                                        <option value="MIDDLECENTER">MIDDLECENTER</option>
                                        <option value="MIDDLERIGHT">MIDDLERIGHT</option>
                                        <option value="BOTTOMLEFT">BOTTOMLEFT</option>
                                        <option value="BOTTOMCENTER">BOTTOMCENTER</option>
                                        <option value="BOTTOMRIGHT">BOTTOMRIGHT</option>
                                        <option value="CUSTOMIZE">CUSTOMIZE</option>
                                    </select>
                                </label>
                            </div>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Height</span>
                                    <input name="height" type="number" min="1" value={signature.height} onChange={updateSignature} />
                                </label>
                                <label className="esign-field">
                                    <span>Width</span>
                                    <input name="width" type="number" min="1" value={signature.width} onChange={updateSignature} />
                                </label>
                                <label className="esign-field">
                                    <span>File TTL</span>
                                    <input name="fileTtl" value={signForm.fileTtl} onChange={updateSignForm} />
                                </label>
                            </div>

                            {signature.signaturePosition === 'CUSTOMIZE' ? (
                                <div className="esign-field-row">
                                    <label className="esign-field">
                                        <span>X coordinate</span>
                                        <input name="xCoordinate" type="number" value={signature.xCoordinate} onChange={updateSignature} />
                                    </label>
                                    <label className="esign-field">
                                        <span>Y coordinate</span>
                                        <input name="yCoordinate" type="number" value={signature.yCoordinate} onChange={updateSignature} />
                                    </label>
                                </div>
                            ) : null}

                            <button className="esign-primary" type="submit" disabled={signing}>
                                {signing ? 'Signing...' : 'Sign PDF'}
                            </button>
                        </form>

                        {signError ? <p className="esign-message esign-error">{signError}</p> : null}
                        {signResult ? (
                            <div className="esign-result">
                                <p>Signed PDF URL</p>
                                <a href={signResult.signedPdf}>{signResult.signedPdf}</a>
                            </div>
                        ) : null}
                    </section>

                    <section className="esign-card esign-card-secondary">
                        <div className="esign-card-head">
                            <h2>Generate Certificate ID</h2>
                            <p>Uses `POST /api/esign/certificate`.</p>
                        </div>
                        <form className="esign-form" onSubmit={handleCertificateSubmit}>
                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Signer name</span>
                                    <input name="signerName" value={certificateForm.signerName} onChange={updateCertificateForm} placeholder="Your Company Name" />
                                </label>
                                <label className="esign-field">
                                    <span>Designation</span>
                                    <input name="signerDesignation" value={certificateForm.signerDesignation} onChange={updateCertificateForm} placeholder="Authorized Signatory" />
                                </label>
                            </div>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Certificate type</span>
                                    <select name="certificateType" value={certificateForm.certificateType} onChange={updateCertificateForm}>
                                        <option value="ORG">ORG</option>
                                        <option value="INDIVIDUAL">INDIVIDUAL</option>
                                    </select>
                                </label>
                                <label className="esign-field">
                                    <span>Signature option</span>
                                    <select name="signatureOptions" value={certificateForm.signatureOptions} onChange={updateCertificateForm}>
                                        <option value="docSignerClass3">docSignerClass3</option>
                                        <option value="docSignerClass2">docSignerClass2</option>
                                    </select>
                                </label>
                            </div>

                            <div className="esign-field-row">
                                <label className="esign-field">
                                    <span>Reason</span>
                                    <input name="reason" value={certificateForm.reason} onChange={updateCertificateForm} />
                                </label>
                                <label className="esign-field">
                                    <span>Location</span>
                                    <input name="location" value={certificateForm.location} onChange={updateCertificateForm} />
                                </label>
                            </div>

                            <button className="esign-secondary" type="submit" disabled={creatingCertificate}>
                                {creatingCertificate ? 'Generating...' : 'Generate Certificate ID'}
                            </button>
                        </form>

                        {certificateError ? <p className="esign-message esign-error">{certificateError}</p> : null}
                        {certificateResult ? (
                            <div className="esign-result">
                                <p>Certificate ID</p>
                                <code>{certificateResult}</code>
                            </div>
                        ) : null}
                    </section>
                </div>
            </div>
        </div>
    );
}

export default ESignPage;
